import React, { useState, useRef } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { parse, isValid } from 'date-fns';
import { pl } from 'date-fns/locale';

const DISPLAY_DATETIME_FORMAT = 'dd-MM-yyyy HH:mm';

interface EditableDateInputProps {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
  hasValidationError?: boolean;
}

export const EditableDateInput: React.FC<EditableDateInputProps> = ({
  value,
  onChange,
  onSave,
  onCancel,
  hasValidationError = false,
}) => {
  const initialValueRef = useRef(value);

  const parseValue = (val: any): Date | null => {
    if (!val) return null;
    if (typeof val === 'number') {
      return new Date(val);
    }
    if (typeof val === 'string') {
      let parsed = parse(val, DISPLAY_DATETIME_FORMAT, new Date());
      if (isValid(parsed)) return parsed;

      parsed = new Date(val);
      if (isValid(parsed)) return parsed;
    }
    return null;
  };

  const [dateValue, setDateValue] = useState<Date | null>(() =>
    parseValue(value),
  );

  const handleDateChange = (newValue: Date | null) => {
    setDateValue(newValue);
    if (newValue && isValid(newValue)) {
      onChange(newValue.getTime());
    } else {
      onChange(null);
    }
  };

  const handleAccept = (newValue: Date | null) => {
    const newTimestamp =
      newValue && isValid(newValue) ? newValue.getTime() : null;

    let initialTimestamp: number | null = null;
    if (initialValueRef.current) {
      if (typeof initialValueRef.current === 'number') {
        initialTimestamp = initialValueRef.current;
      } else if (typeof initialValueRef.current === 'string') {
        let parsedDate = parse(
          initialValueRef.current,
          DISPLAY_DATETIME_FORMAT,
          new Date(),
        );
        if (!isValid(parsedDate)) {
          parsedDate = new Date(initialValueRef.current);
        }
        initialTimestamp = isValid(parsedDate) ? parsedDate.getTime() : null;
      }
    }

    const normalizeToMinutes = (timestamp: number | null): number | null => {
      if (timestamp === null) return null;
      const d = new Date(timestamp);
      d.setSeconds(0, 0);
      return d.getTime();
    };

    const normalizedNew = normalizeToMinutes(newTimestamp);
    const normalizedInitial = normalizeToMinutes(initialTimestamp);

    if (normalizedNew === normalizedInitial) {
      onCancel();
    } else {
      onSave(newTimestamp);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onCancel();
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      const currentTimestamp =
        dateValue && isValid(dateValue) ? dateValue.getTime() : null;

      let initialTimestamp: number | null = null;
      if (initialValueRef.current) {
        if (typeof initialValueRef.current === 'number') {
          initialTimestamp = initialValueRef.current;
        } else if (typeof initialValueRef.current === 'string') {
          let parsedDate = parse(
            initialValueRef.current,
            DISPLAY_DATETIME_FORMAT,
            new Date(),
          );
          if (!isValid(parsedDate)) {
            parsedDate = new Date(initialValueRef.current);
          }
          initialTimestamp = isValid(parsedDate) ? parsedDate.getTime() : null;
        }
      }

      const normalizeToMinutes = (timestamp: number | null): number | null => {
        if (timestamp === null) return null;
        const date = new Date(timestamp);
        date.setSeconds(0, 0);
        return date.getTime();
      };

      const normalizedCurrent = normalizeToMinutes(currentTimestamp);
      const normalizedInitial = normalizeToMinutes(initialTimestamp);

      if (normalizedCurrent !== normalizedInitial) {
        onSave(currentTimestamp);
      }
    }, 200);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <DateTimePicker
        value={dateValue}
        onChange={handleDateChange}
        onAccept={handleAccept}
        format='dd-MM-yyyy HH:mm'
        ampm={false}
        minutesStep={15}
        slotProps={{
          field: { clearable: true },
          textField: {
            variant: 'outlined',
            size: 'small',
            autoFocus: true,
            onKeyDown: handleKeyDown,
            onBlur: handleBlur,
            sx: {
              width: '100%',
              margin: 0,
              minWidth: 0,
              position: 'relative',
              zIndex: 0,
              '& .MuiOutlinedInput-root': {
                border: 'none',
                outline: 'none',
                borderBottom: 'none',
                borderRadius: 0,
                background: 'transparent !important',
                backgroundColor: 'transparent !important',
                height: '24px',
                minHeight: '24px',
                padding: 0,
                flexWrap: 'nowrap',
                position: 'relative',
                zIndex: 0,
                '&:hover': {
                  background: 'transparent !important',
                  backgroundColor: 'transparent !important',
                },
                '&.Mui-focused': {
                  border: 'none',
                  outline: 'none',
                  background: 'transparent !important',
                  backgroundColor: 'transparent !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              },
              '& .MuiInputBase-input': {
                padding: '0px 2px',
                paddingLeft: '2px',
                background: 'transparent !important',
                backgroundColor: 'transparent !important',
                color: 'inherit',
                fontSize: '0.7rem',
                lineHeight: '1.5em',
                height: '24px',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 0,
                flex: '1 1 0',
                minWidth: '0 !important',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': {
                  background: 'transparent !important',
                  backgroundColor: 'transparent !important',
                },
                '&:focus': {
                  background: 'transparent !important',
                  backgroundColor: 'transparent !important',
                },
              },
              '& .MuiInputAdornment-root': {
                flexShrink: 0,
                marginLeft: '2px',
              },
              '& .MuiInputAdornment-root:last-of-type': {
                marginLeft: '10px',
                ...(hasValidationError && { marginRight: '26px' }),
              },
              '& .MuiInputAdornment-root .MuiIconButton-root': {
                padding: '1px',
                margin: 0,
              },
              '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                fontSize: '15px',
              },
            },
          },
          popper: {
            sx: {
              '& .MuiMultiSectionDigitalClock-root': {
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              },
              '& .MuiList-root': {
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
